import { useMemo } from 'react';
import _groupBy from 'lodash/groupBy';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import { useStore } from '@thd-nucleus/experience-context';
import { useProductDataStore, usePromoStore } from '../store';
import { useDesignStore } from '../store/design/context';
import { checkFulfillment, getFulfillmentMethodAndLocation, getLocalizerZipCode } from '../utils/fulfillment-utils';

const fullReset = (updateFulfillmentProducts, resetPromoItems, resetCart, addAccessory, updateTotalPrice) => {
  updateFulfillmentProducts([]);
  resetPromoItems();
  resetCart();
  addAccessory(false);
  return updateTotalPrice({
    value: '0.00',
    dollarOff: '0.00',
    percentOff: '0',
    originalPrice: '0.00'
  });
};

// used to be handleFulfillment
const generateFulfillmentProducts = (storeProductData, allProducts, storeId, storeZip, zipCode) => {
  const { products, cart } = allProducts;
  const groupedProducts = _groupBy(products, 'Sku');
  const allVisualizedProducts = _map(groupedProducts, (group) => {
    const product = group[0];
    const fulfillmentInfo = checkFulfillment(storeProductData?.[product.Sku], zipCode);
    const [fulfillmentMethod, fulfillmentLocation] = getFulfillmentMethodAndLocation(
      fulfillmentInfo, storeId, storeZip, zipCode
    );
    return ({
      itemId: product.Sku,
      storeSkuNumber: storeProductData?.[product.Sku]?.identifiers?.storeSkuNumber,
      omsThdSku: storeProductData?.[product.Sku]?.identifiers?.omsThdSku,
      parentId: storeProductData?.[product.Sku]?.identifiers?.parentId,
      fulfillmentLocation,
      fulfillmentMethod,
      quantity: groupedProducts[product.Sku].length,
      quantityLimit: storeProductData?.[product.Sku]?.info?.quantityLimit,
      unitPrice: storeProductData?.[product.Sku]?.pricing?.value,
      storeId,
      zipCode,
      outsideVisualizer: false,
      hasPromos: !_isEmpty(storeProductData?.[product.Sku]?.pricing?.conditionalPromotions)
    });
  });
  const allNonVisualizedProducts = _map(cart, (product) => {
    const fulfillmentInfo = checkFulfillment(storeProductData?.[product.Sku], zipCode);
    const [fulfillmentMethod, fulfillmentLocation] = getFulfillmentMethodAndLocation(
      fulfillmentInfo, storeId, storeZip, zipCode
    );
    return ({
      itemId: product.Sku,
      storeSkuNumber: storeProductData?.[product.Sku]?.identifiers?.storeSkuNumber,
      omsThdSku: storeProductData?.[product.Sku]?.identifiers?.omsThdSku,
      parentId: storeProductData?.[product.Sku]?.identifiers?.parentId,
      fulfillmentLocation,
      fulfillmentMethod,
      quantity: product.Quantity,
      quantityLimit: storeProductData?.[product.Sku]?.info?.quantityLimit,
      unitPrice: storeProductData?.[product.Sku]?.pricing?.value,
      storeId,
      zipCode,
      outsideVisualizer: true,
      hasPromos: true
    });
  });
  return [...allVisualizedProducts, ...allNonVisualizedProducts];
};

// used to be handleTotalPrice
// now does total price and fulfillment all in one
export default () => {
  const { storeId, storeZip } = useStore();
  const zipCode = getLocalizerZipCode();
  const productData = useProductDataStore((state) => state.productData);

  const products = useDesignStore((state) => state.design.products);
  const cart = useDesignStore((state) => state.design.cart);
  const resetCart = useDesignStore((state) => state.resetCart);
  const discounts = usePromoStore((state) => state.discounts);
  const promoTotal = usePromoStore((state) => state.promoTotal);
  const updateTotalPrice = usePromoStore((state) => state.updateTotalPrice);
  const resetPromoItems = usePromoStore((state) => state.resetPromoItems);
  const updateFulfillmentProducts = usePromoStore((state) => state.updateFulfillmentProducts);
  const addAccessory = useDesignStore((state) => state.addAccessory);
  const hasAccessories = useDesignStore((state) => state.design.hasAccessories);

  useMemo(() => {
    if (!_isEmpty(productData)) {
      if (_isEmpty(cart) && !hasAccessories && _isEmpty(products)) {
        return fullReset(updateFulfillmentProducts, resetPromoItems, resetCart, addAccessory, updateTotalPrice);
      }

      const allAddedProducts = { products, cart };
      const allProducts = [...allAddedProducts.products, ...allAddedProducts.cart];
      const containsPromoProduct = _find(
        allProducts,
        (product) => productData?.[product.Sku]?.pricing?.conditionalPromotions?.length > 0
      );

      const totalPriceCalculation = _reduce(
        allProducts,
        (prev, curr) => {
          const value = productData?.[curr.Sku]?.pricing.value || 0;
          const original = productData?.[curr.Sku]?.pricing?.original || 0;
          const qty = curr.Quantity || 1;
          return ({
            value: prev.value + (value * qty),
            original: prev.original + (original * qty || (value * qty)),
            dollarOff: (prev.original && original)
              ? (prev.original - prev.value) + ((original * qty) - (value * qty))
              : 0
          });
        }, { value: 0, original: 0, dollarOff: 0 });

      const discountAmount = _reduce(discounts?.cartResponse?.items, (prev, curr) => {
        const totalLineItemDiscount = curr?.discounts?.totalLineItemDiscAmt || 0;
        return prev + Math.abs(totalLineItemDiscount);
      }, 0);
      const fulfillmentProducts = generateFulfillmentProducts(
        productData, allAddedProducts, storeId, storeZip, zipCode
      );
      updateFulfillmentProducts(fulfillmentProducts);
      const {
        value: tpcValue,
        original: tpcOriginal,
      } = totalPriceCalculation;
      const totalDollarOff = Math.abs(((tpcOriginal - tpcValue) + discountAmount));
      const totalPercentOff = Math.abs(Math.round(Number((totalDollarOff / tpcOriginal) * 100)));

      if (_isEmpty(containsPromoProduct) && !_isEmpty(discounts)) {
        resetPromoItems();
      }

      return updateTotalPrice({
        value: (tpcValue - discountAmount).toFixed(2),
        dollarOff: Number(totalDollarOff)
          ? Math.abs(totalDollarOff)
          : 0,
        percentOff: Math.abs(totalPercentOff),
        originalPrice: tpcOriginal.toFixed(2)
      });
    }

    return fullReset(
      updateFulfillmentProducts,
      resetPromoItems,
      resetCart,
      addAccessory,
      updateTotalPrice
    );
  }, [productData, products, cart, promoTotal, discounts]);
};
